import React, { useEffect, useLayoutEffect } from "react";
import { PerspectiveCamera, OrthographicCamera } from "@react-three/drei";
import GridView from "./Grid";
import { useGlobalStore } from "@/store";
import { useFrame, useThree } from "@react-three/fiber";
import {
  initializeClickManager,
  setWebGLRenderer,
} from "@/helpers/clickManager";
import { objectsLookAtCamera } from "@/helpers/ipr";
import { checkCameraListenerExistence } from "./Mesh";
import * as THREE from "three";
export let cameraControlsHolder;
export let perspectiveCamera, orthographicCamera;
export let isCameraDragging = false;
export function getIsCameraDragging() {
  return isCameraDragging;
}
let once = true,
  isCameraControlsListenerAssgined = false;
let gl;

export function setOrbitControlsEnabled(state = true) {
  if (!cameraControlsHolder) {
    return;
  }
  cameraControlsHolder.current._isDragging = state;
}
function Camera({
  orthographicCameraRef,
  perspectiveCameraRef,
  grid,
  isMobile,
  orientation,
  tpLoaded,
  view,
  cameraControls,
}) {
  const { toggle, refs } = useGlobalStore(state => ({
    toggle: state.toggle,
    refs: state.refs,
  }));
  if (cameraControls) {
    cameraControlsHolder = cameraControls;

    if (
      !isCameraControlsListenerAssgined ||
      !checkCameraListenerExistence(
        cameraControlsHolder.current,
        "objectsLookAtCamera",
        "update",
      )
    ) {
      cameraControlsHolder.current.addEventListener(
        "update",
        objectsLookAtCamera,
      );

      cameraControlsHolder.current.addEventListener("rest", () => {
        isCameraDragging = false;
      });

      cameraControlsHolder.current.addEventListener("sleep", () => {
        isCameraDragging = false;
      });

      cameraControlsHolder.current.addEventListener("control", () => {
        isCameraDragging = true;
      });
      isCameraControlsListenerAssgined = true;
    }
  }

  const scene = useThree().scene;
  gl = useThree().gl;
  useEffect(() => {
    if (once) {
      initializeClickManager(scene, perspectiveCameraRef.current, gl, 2);
      perspectiveCamera = perspectiveCameraRef.current;
      orthographicCamera = orthographicCameraRef.current;
      addEventListener("resize", event => {
        setWebGLRenderer(gl);
      });
      once = false;
    }
  }, []);

  useEffect(() => {
    orthographicCameraRef.current.position.set(0, 0, 100);
    perspectiveCameraRef.current.position.set(0, 0, 200);
  }, [grid]);
  useLayoutEffect(() => {
    refs.controlsRef.current.setFocalOffset(
      toggle.startRevision ? 25 : 0,
      0,
      0,
      true,
    );
  }, [toggle.startRevision, view]);

  useEffect(() => {
    // Mobile pinch-zoom handlers
    let touchStartDistance = 0;
    let initialZoom = 1;

    const handleTouchStart = e => {
      if (e.touches.length === 2) {
        // Calculate initial distance between two fingers
        touchStartDistance = Math.hypot(
          e.touches[0].clientX - e.touches[1].clientX,
          e.touches[0].clientY - e.touches[1].clientY,
        );

        // Store initial zoom value
        initialZoom = refs.controlsRef.current?._camera.zoom || 1;
      }
    };

    const handleTouchMove = e => {
      if (!refs.controlsRef.current || e.touches.length !== 2) return;

      // Prevent default touch behavior (scrolling/zooming)
      // Calculate current distance between fingers
      const currentDistance = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY,
      );

      // Calculate zoom delta based on pinch distance change
      const delta = currentDistance - touchStartDistance;
      touchStartDistance = currentDistance;

      const isPerspective =
        refs.controlsRef.current._camera instanceof THREE.PerspectiveCamera;

      // Adjust sensitivity for different camera types
      const sensitivity = isPerspective ? 0.008 : 0.02;
      const newZoom = initialZoom + delta * sensitivity;

      // Apply zoom limits
      const maxZoom =
        refs.controlsRef.current.maxZoom * (isPerspective ? 1 : 2);
      const minZoom =
        refs.controlsRef.current.minZoom * (isPerspective ? 1 : 1);

      const clampedZoom = Math.max(minZoom, Math.min(maxZoom, newZoom));

      // Update both controls
      refs.controlsRef.current.zoomTo(clampedZoom, true);
      refs.controlsRef2.current.zoomTo(clampedZoom, true);

      // Update initial zoom for smooth continuous movement
      initialZoom = clampedZoom;
    };

    // Add event listeners
    window.addEventListener("touchstart", handleTouchStart);
    window.addEventListener("touchmove", handleTouchMove, { passive: false });

    return () => {
      window.removeEventListener("touchstart", handleTouchStart);
      window.removeEventListener("touchmove", handleTouchMove);
    };
  }, []);
  return (
    <>
      <OrthographicCamera
        name="orthographic"
        visible={grid ? true : false}
        makeDefault={grid ? true : false}
        ref={orthographicCameraRef}
        left={window.innerWidth / -2}
        right={window.innerWidth / 2}
        top={window.innerHeight / 2}
        bottom={window.innerHeight / -2}
        zoom={
          isMobile
            ? orientation === "Portrait"
              ? 5.5
              : 6.5
            : window.innerWidth > 1900
              ? 12
              : 9.5
        }
        position={[0, 0, 100]}
      >
        <GridView grid={grid} />
      </OrthographicCamera>

      <PerspectiveCamera
        name="perspsective"
        visible={grid ? false : true}
        ref={perspectiveCameraRef}
        makeDefault={grid ? false : true}
        fov={25}
        zoom={isMobile ? (orientation === "Portrait" ? 0.75 : 1.35) : 1}
        near={100}
        far={250}
        position={[0, 0, 0]}
      ></PerspectiveCamera>
    </>
  );
}

export default Camera;
